// src/components/ecommerce/CheckoutModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  CreditCard, 
  Truck, 
  User, 
  MapPin,
  Phone,
  Mail,
  Shield,
  AlertCircle,
  Loader2,
  CheckCircle,
  Receipt,
  Copy,
  ExternalLink
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { PaymentStep } from './PaymentStep';
import { CustomerInfoStep } from './CustomerInfoStep';
import { ShippingStep } from './ShippingStep';
import { OrderConfirmation } from './OrderConfirmation';
import toast from 'react-hot-toast';

const CHECKOUT_STEPS = {
  CUSTOMER_INFO: 'customer',
  SHIPPING: 'shipping',
  PAYMENT: 'payment',
  CONFIRMATION: 'confirmation'
};

export const CheckoutModal = ({
  isOpen,
  onClose,
  cart,
  storeSettings,
  orderSummary,
  onOrderComplete
}) => {
  const [currentStep, setCurrentStep] = useState(CHECKOUT_STEPS.CUSTOMER_INFO);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState({
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    shipping: {
      address: '',
      city: '',
      province: '',
      postalCode: '',
      deliveryZone: orderSummary.selectedDeliveryZone,
      deliveryNotes: ''
    },
    payment: {
      method: null,
      details: {}
    }
  });
  const [createdOrder, setCreatedOrder] = useState(null);

  const steps = [
    {
      id: CHECKOUT_STEPS.CUSTOMER_INFO,
      title: 'Customer Info',
      icon: User,
      component: CustomerInfoStep
    },
    {
      id: CHECKOUT_STEPS.SHIPPING,
      title: 'Delivery',
      icon: Truck,
      component: ShippingStep
    },
    {
      id: CHECKOUT_STEPS.PAYMENT,
      title: 'Payment',
      icon: CreditCard,
      component: PaymentStep
    },
    {
      id: CHECKOUT_STEPS.CONFIRMATION,
      title: 'Complete',
      icon: CheckCircle,
      component: OrderConfirmation
    }
  ];

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const CurrentStepComponent = steps[currentStepIndex]?.component;

  const handleNext = async () => {
    if (currentStep === CHECKOUT_STEPS.PAYMENT) {
      await handleOrderSubmit();
    } else if (!isLastStep) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  console.log('Order from', storeSettings.id);

  const handleOrderSubmit = async () => {
    setLoading(true);
    try {
      const order = {
        ...orderData,
        items: cart,
        summary: orderSummary,
        storeId: storeSettings.id,
        status: 'pending',
        createdAt: new Date(),
        orderNumber: `ORD-${Date.now()}`
      };

      // Create order in Firestore
      const createdOrderId = await ecommerceService.createOrder(storeSettings.id, order);
      const orderWithId = { ...order, id: createdOrderId };
      setCreatedOrder(orderWithId);

      // Handle payment processing based on method
      if (orderData.payment.method === 'payfast') {
        await handlePayFastPayment(orderWithId);
      } else if (orderData.payment.method === 'eft') {
        await handleEFTPayment(orderWithId);
      } else if (orderData.payment.method === 'cod') {
        await handleCODPayment(orderWithId);
      }

      setCurrentStep(CHECKOUT_STEPS.CONFIRMATION);
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFastPayment = async (order) => {
    // PayFast integration
    const paymentData = {
      merchant_id: storeSettings.paymentMethods?.payfast?.merchantId,
      merchant_key: storeSettings.paymentMethods?.payfast?.merchantKey,
      amount: order.summary.grandTotal.toFixed(2),
      item_name: `Order ${order.orderNumber}`,
      item_description: `${cart.length} items`,
      return_url: `${window.location.origin}/order-complete/${order.id}`,
      cancel_url: `${window.location.origin}/checkout`,
      notify_url: `${window.location.origin}/api/payfast-notify`,
      name_first: order.customer.firstName,
      name_last: order.customer.lastName,
      email_address: order.customer.email,
      cell_number: order.customer.phone
    };

    // In a real implementation, you would redirect to PayFast
    console.log('PayFast payment data:', paymentData);
  };

  const handleEFTPayment = async (order) => {
    // EFT payment just updates order status to awaiting payment
    await ecommerceService.updateOrder(order.id, {
      status: 'awaiting-payment',
      paymentMethod: 'eft'
    });
  };

  const handleCODPayment = async (order) => {
    // COD payment updates order status to confirmed
    await ecommerceService.updateOrder(order.id, {
      status: 'confirmed',
      paymentMethod: 'cod'
    });
  };

  const updateOrderData = (step, data) => {
    setOrderData(prev => ({
      ...prev,
      [step]: { ...prev[step], ...data }
    }));
  };

  const isStepValid = () => {
    switch (currentStep) {
      case CHECKOUT_STEPS.CUSTOMER_INFO:
        return orderData.customer.firstName?.trim() && 
               orderData.customer.lastName?.trim() && 
               orderData.customer.email?.trim() && 
               orderData.customer.phone?.trim();
               
      case CHECKOUT_STEPS.SHIPPING:
        // More comprehensive shipping validation
        const hasRequiredAddress = orderData.shipping.address?.trim() && 
                                 orderData.shipping.city?.trim();
        
        const hasDeliveryZone = orderData.shipping.deliveryZone && 
                              orderData.shipping.deliveryZone.name;
        
        // If pickup is selected, we don't need full address
        const isPickup = orderData.shipping.deliveryZone?.name === 'pickup';
        
        if (isPickup) {
          return hasDeliveryZone;
        } else {
          return hasRequiredAddress && hasDeliveryZone;
        }
        
      case CHECKOUT_STEPS.PAYMENT:
        return orderData.payment.method;
        
      default:
        return true;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Checkout</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.id === currentStep;
                const isCompleted = index < currentStepIndex;
                
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors
                      ${isActive ? 'border-blue-600 bg-blue-600 text-white' : 
                        isCompleted ? 'border-green-600 bg-green-600 text-white' : 
                        'border-gray-300 bg-white text-gray-400'}
                    `}>
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      isActive ? 'text-blue-600' : 
                      isCompleted ? 'text-green-600' : 
                      'text-gray-500'
                    }`}>
                      {step.title}
                    </span>
                    {index < steps.length - 1 && (
                      <div className={`w-12 h-0.5 mx-4 ${
                        isCompleted ? 'bg-green-600' : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    orderData={orderData}
                    onUpdate={updateOrderData}
                    storeSettings={storeSettings}
                    cart={cart}
                    orderSummary={orderSummary}
                    createdOrder={createdOrder}
                  />
                )}
              </div>

              {/* Order Summary Sidebar */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                
                {/* Cart Items */}
                <div className="space-y-3 mb-4">
                  {cart.map((item) => (
                    <div key={`${item.id}-${item.variant || 'default'}`} 
                         className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-medium">R{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Order Totals */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>R{orderSummary.subtotal.toFixed(2)}</span>
                  </div>
                  
                  {orderSummary.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-R{orderSummary.discount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {storeSettings.shipping?.enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivery</span>
                      <span>R{orderSummary.shipping.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {orderSummary.tax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VAT</span>
                      <span>R{orderSummary.tax.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
                    <span>Total</span>
                    <span>R{orderSummary.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Navigation */}
          {currentStep !== CHECKOUT_STEPS.CONFIRMATION && (
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <button
                onClick={handlePrevious}
                disabled={isFirstStep}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  isFirstStep 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>
              
              <button
                onClick={handleNext}
                disabled={!isStepValid() || loading}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  !isStepValid() || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {currentStep === CHECKOUT_STEPS.PAYMENT ? 'Place Order' : 'Next'}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};